import { useEffect } from 'react';
import { useRouter } from 'next/compat/router';
import { setInstoreCartTrackerContextFromQuery } from '../utils';

type UseCartRoutingProps = {
  isUserLoading: boolean;
  isLoggedIn: boolean;
  isEnrolledDutchiePay: boolean;
  enrollmentStatusPending: boolean;
  userNotenrolled: boolean;
  isMissingUserInfo: boolean;
  dispensaryIdQueryString: string[] | string | undefined;
};

export const useCartRouting = ({
  isUserLoading,
  isLoggedIn,
  enrollmentStatusPending,
  userNotenrolled,
  isMissingUserInfo,
  dispensaryIdQueryString,
}: UseCartRoutingProps): void => {
  const router = useRouter();

  useEffect(() => {
    if (
      router?.isReady &&
      ((!enrollmentStatusPending && userNotenrolled && !isMissingUserInfo) || (!isUserLoading && !isLoggedIn))
    ) {
      const dispensaryIdInQueryString = !!dispensaryIdQueryString && typeof dispensaryIdQueryString === 'string';

      const validParams = [
        'cartToken',
        'checkoutToken',
        'isKiosk',
        'orderId',
        'updateOrderToken',
        'isHub',
        'source',
        'message_stage',
      ];
      const searchParams = validParams.reduce((acc, param) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
        const value = router?.query[param];
        if (value && typeof value === 'string') {
          acc.append(param, value);
        }
        return acc;
      }, new URLSearchParams());

      // Link to Pay only (source present): mark the return so the instore-cart-controller can attribute
      // the post-signup landing's dutchiePayLinkToPayInitiated event as entry_point=post_signup.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
      const sourceParam = router?.query.source;
      if (sourceParam === 'email_cta' || sourceParam === 'sms_cta') {
        searchParams.append('entry_point', 'post_signup');
      }

      const queryString = searchParams.toString();
      const refUrl = `/pay/instore${queryString ? `?${queryString}` : ''}`;
      const query = dispensaryIdInQueryString ? { refUrl, cname: dispensaryIdQueryString } : { refUrl };

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
      setInstoreCartTrackerContextFromQuery(router?.query ?? {});

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
      void router?.push({
        pathname: `/pay/signup`,
        query,
      });
    }
  }, [
    router?.isReady,
    enrollmentStatusPending,
    userNotenrolled,
    isMissingUserInfo,
    isUserLoading,
    isLoggedIn,
    router,
    dispensaryIdQueryString,
  ]);
};
